
// Vue
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";

// Components
import Multiselect from "vue-multiselect";
import { DeviceDetails } from "@sureview/v2-cameras";
import vselect3 from "vselect3";

// Utils
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

// Types
import { ManualPulseRequest } from "@/store/manual-actions/types";
import { Region } from "@/store/types";
import { CancelTokenSource } from "axios";

import { debounce } from "lodash";

// Vuex Modules
const Eventqueue = namespace("eventqueue");
const Devices = namespace("devices");
const ManualAction = namespace("manualaction");

@Component({
	mixins: [validationMixin],
	validations: {
		manualPulseRequest: {
			device: {
				deviceId: {
					required
				}
			},
			reason: {
				required
			}
		}
	},
	components: {
		multiselect: Multiselect,
		vselect3: vselect3
	}
})
export default class ManualPulse extends Vue {
	@Prop({ default: false, type: Boolean }) public valid: boolean;
	@Prop({ default: -1, type: Number }) public tabIndex: number;
	@Prop({ default: "", type: String }) public readerGroupName: string;
	@Prop({ default: "", type: String }) public readerGroupError: string;
	@Prop({ default: false, type: Boolean }) public loadingReaderGroup: boolean;
	@Prop({ default: null }) public region: Region;
	@Prop({ default: false, type: Boolean }) canUserInteractWithPlugin: boolean;

	@Getter private getFeature: (features: string[]) => boolean;
	@Eventqueue.Mutation private setManualActionShown: (shown: boolean) => void;
	@ManualAction.Action private setManualPulseData: (data: ManualPulseRequest) => Promise<void>;

	@Devices.State private cancelTokenSource: CancelTokenSource;
	@Devices.State private doors: DeviceDetails[];
	@Devices.Mutation private setDoors: (doors: DeviceDetails[]) => void;
	@Devices.Action private deviceSearch: ({
		search,
		regionUrl
	}: {
		search: string;
		regionUrl: string;
	}) => Promise<void>;

	private manualPulseRequestTemplate = {
		device: null,
		reason: ""
	};

	public manualPulseRequest = { ...this.manualPulseRequestTemplate };

	/**
	 * If the component is under a tab, reset data when tab changes.
	 */
	@Watch("tabIndex")
	private onTabIndexChanged() {
		this.reset();
	}

	/**
	 * If the selected region changes, reset the selected device.
	 */
	@Watch("region")
	private onSelectedRegionChanged() {
		this.reset();

		// Clear any doors we may have previously searched for.
		this.setDoors([]);
	}

	private reset(): void {
		this.manualPulseRequest = { ...this.manualPulseRequestTemplate };
	}

	/**
	 * De-bounced function which performs a device search, cancelling any
	 * previous requests.
	 */
	private debouncedSearch = debounce(
		async function(query: string, loading: Function) {
			if (this.cancelTokenSource) {
				this.cancelTokenSource.cancel("Operation canceled by the user.");
			}

			if (!query || query.length == 0) {
				this.devices = [];
			}
			else {
				try {
					await this.deviceSearch({ search: query, regionUrl: this.region?.url });
				} catch (err) {
					this.$notify({
						type: "error",
						title: "Quick Control",
						text: "Unable to search for devices, please try again."
					});
				}
			}

			loading(false);
		},
		300,
		{ leading: false, trailing: true }
	);


	/**
	 * Async handler for the vue-multiselect. Handles the real time door / device search.
	 */
	public async asyncFindDevice(query: string, loading: Function) {
		loading(true);
		await this.debouncedSearch(query, loading);
	}

	@Watch("$v.manualPulseRequest", { deep: true })
	private async validationStateChange(validationState: { $invalid: boolean }) {
		this.$emit("update-valid", !validationState.$invalid);
		if (!validationState.$invalid) {
			await this.setManualPulseData({
				deviceId: this.manualPulseRequest.device.deviceId,
				reason: this.manualPulseRequest.reason
			} as ManualPulseRequest);
		}
	}

	public async deviceSelected(device: DeviceDetails) {
		this.$emit("device-selected", device);
	}

	private get displayReaderGroupName() {
		return this.getFeature(["Alarms", "QuickControl", "ShowReaderGroups"]);
	}
}
